import { useEffect, useId, useRef, useState } from "react";
import { Field, SectionLabel, TextInput, phoneNumber } from "./controls";
import {
  autocompleteInputValue,
  bindAutocompletePredictionCapture,
  createBasicPlaceAutocomplete,
  loadGoogleMapsPlaces,
  mapsLookupEnabled,
  parseSelectedPlace,
  placeIdFromSelectEvent,
  rememberAutocompletePrediction,
  resolvePlaceAddressFromId,
} from "../lib/google-maps";
import {
  sameStreetAndZip,
  type StreetAddress,
} from "../lib/place-address";

function fieldName(prefix: string | undefined, name: string): string {
  return prefix ? `${prefix}${name[0]!.toUpperCase()}${name.slice(1)}` : name;
}

function emptyAddress(): StreetAddress {
  return {
    addressLine1: "",
    addressLine2: null,
    city: "",
    state: "",
    postalCode: "",
    lat: null,
    lng: null,
  };
}

export type ExistingAddressMatch = {
  id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  primaryPhone?: string | null;
  preferredPropertyId?: string | null;
  bookingNotes?: string | null;
  matchingProperty?: {
    id: string;
    addressLine1: string;
    addressLine2?: string | null;
    city: string;
    state?: string | null;
    postalCode?: string | null;
  } | null;
  matchingBilling?: {
    addressLine1: string;
    addressLine2?: string | null;
    city: string;
    state?: string | null;
    postalCode?: string | null;
  } | null;
};

export type ExistingAddressHit = {
  key: string;
  row: ExistingAddressMatch;
  kind: "location" | "billing";
  address: {
    addressLine1: string;
    addressLine2?: string | null;
    city: string;
    state?: string | null;
    postalCode?: string | null;
  };
};

export function existingAddressHits(rows: ExistingAddressMatch[]): ExistingAddressHit[] {
  const hits: ExistingAddressHit[] = [];
  for (const row of rows) {
    if (row.matchingProperty) {
      hits.push({
        key: `${row.id}:location:${row.matchingProperty.id}`,
        row,
        kind: "location",
        address: row.matchingProperty,
      });
    }
    if (
      row.matchingBilling &&
      (!row.matchingProperty || !sameStreetAndZip(row.matchingBilling, row.matchingProperty))
    ) {
      hits.push({
        key: `${row.id}:billing`,
        row,
        kind: "billing",
        address: row.matchingBilling,
      });
    }
  }
  return hits;
}

function hitKindLabel(kind: ExistingAddressHit["kind"]): string {
  switch (kind) {
    case "billing":
      return "Billing";
    case "location":
      return "On file";
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

function streetFromHit(hit: ExistingAddressHit): StreetAddress {
  return {
    addressLine1: hit.address.addressLine1,
    addressLine2: hit.address.addressLine2 ?? null,
    city: hit.address.city,
    state: (hit.address.state ?? "").trim(),
    postalCode: hit.address.postalCode ?? "",
    lat: null,
    lng: null,
  };
}

export function AddressFields({
  prefix,
  legend,
  enableLookup = false,
  searchExisting,
  onExistingSelect,
  onResolved,
}: {
  prefix?: string;
  legend?: string;
  enableLookup?: boolean;
  searchExisting?: (query: string) => Promise<ExistingAddressMatch[]>;
  onExistingSelect?: (row: ExistingAddressMatch) => void;
  onResolved?: (address: StreetAddress) => void;
}) {
  const field = (name: string) => fieldName(prefix, name);
  const streetLabelId = useId();
  const hostRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const [values, setValues] = useState<StreetAddress>(emptyAddress);
  const [lookupReady, setLookupReady] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [zipError, setZipError] = useState("");
  const [resolvingPlace, setResolvingPlace] = useState(false);
  const [placeId, setPlaceId] = useState<string | null>(null);
  const [picked, setPicked] = useState(false);
  const [existing, setExisting] = useState<ExistingAddressMatch[]>([]);
  const [existingReady, setExistingReady] = useState(true);
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;
  const onExistingSelectRef = useRef(onExistingSelect);
  onExistingSelectRef.current = onExistingSelect;
  const googleSelectLockRef = useRef(false);
  const hits = existingAddressHits(existing);
  const hitsRef = useRef(hits);
  hitsRef.current = hits;
  const streetRef = useRef(values.addressLine1);
  streetRef.current = values.addressLine1;
  const needle = values.addressLine1.trim();
  const useGoogleStreet = enableLookup && mapsLookupEnabled() && lookupReady && !picked;

  useEffect(() => {
    if (!enableLookup || !mapsLookupEnabled()) return;
    let live = true;
    void loadGoogleMapsPlaces()
      .then(() => {
        if (live) setLookupReady(true);
      })
      .catch(() => {
        if (live) setLookupReady(false);
      });
    return () => {
      live = false;
    };
  }, [enableLookup]);

  useEffect(() => {
    if (!enableLookup || !searchExisting || picked) {
      setExisting([]);
      setExistingReady(true);
      return;
    }
    if (needle.length < 3) {
      setExisting([]);
      setExistingReady(true);
      return;
    }
    let live = true;
    setExistingReady(false);
    const timer = window.setTimeout(() => {
      void searchExisting(needle)
        .then((rows) => {
          if (!live) return;
          setExisting(rows);
          setExistingReady(true);
        })
        .catch(() => {
          if (!live) return;
          setExisting([]);
          setExistingReady(true);
        });
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [enableLookup, searchExisting, needle, picked]);

  useEffect(() => {
    if (!useGoogleStreet) return;
    const host = hostRef.current;
    if (!host) return;
    let cancelled = false;
    let element: HTMLElement | null = null;
    let onSelect: ((event: Event) => void) | null = null;
    let onInput: ((event: Event) => void) | null = null;
    let onError: (() => void) | null = null;
    let unbindCapture: (() => void) | null = null;
    void createBasicPlaceAutocomplete({
      placeholder: "Start typing the street",
      ariaLabel: "Street address",
    }).then((autocomplete) => {
      if (cancelled) {
        autocomplete.remove();
        return;
      }
      element = autocomplete;
      const seed = streetRef.current;
      if (seed) {
        const seeded = autocomplete as HTMLElement & { value?: string };
        seeded.value = seed;
        if (autocomplete instanceof HTMLInputElement) autocomplete.value = seed;
      }
      onSelect = (event: Event) => {
        if (hitsRef.current.length > 0) return;
        googleSelectLockRef.current = true;
        rememberAutocompletePrediction(autocomplete, event);
        const fromPrediction = parseSelectedPlace(event, autocomplete);
        const selected = placeIdFromSelectEvent(event);
        if (fromPrediction?.addressLine1 && fromPrediction.city) {
          setValues(fromPrediction);
          setPicked(true);
          setExisting([]);
          setLookupError("");
          setPlaceId(null);
          if (fromPrediction.postalCode || !selected || !detailsRef.current) {
            googleSelectLockRef.current = false;
            setResolvingPlace(false);
            setZipError("");
            onResolvedRef.current?.(fromPrediction);
            return;
          }
          setResolvingPlace(true);
          setZipError("");
          void resolvePlaceAddressFromId(selected, detailsRef.current, {
            requirePostalCode: true,
          }).then((details) => {
            googleSelectLockRef.current = false;
            setResolvingPlace(false);
            if (!details?.postalCode) {
              setZipError("Enter ZIP.");
              onResolvedRef.current?.(fromPrediction);
              return;
            }
            const filled: StreetAddress = {
              ...fromPrediction,
              postalCode: details.postalCode,
              lat: details.lat ?? fromPrediction.lat,
              lng: details.lng ?? fromPrediction.lng,
              state: fromPrediction.state || details.state,
            };
            setValues(filled);
            setZipError("");
            onResolvedRef.current?.(filled);
          });
          return;
        }
        if (!selected) {
          googleSelectLockRef.current = false;
          return;
        }
        setLookupError("");
        setPlaceId(selected);
      };
      onInput = (event: Event) => {
        if (googleSelectLockRef.current) return;
        const query = autocompleteInputValue(autocomplete, event);
        setPicked(false);
        setPlaceId(null);
        setLookupError("");
        setValues((current) => ({ ...current, addressLine1: query }));
      };
      onError = () => {
        setLookupError("Street lookup is unavailable. Enter the address manually.");
      };
      unbindCapture = bindAutocompletePredictionCapture(autocomplete);
      if (cancelled) {
        unbindCapture();
        autocomplete.remove();
        return;
      }
      autocomplete.addEventListener("gmp-select", onSelect, true);
      autocomplete.addEventListener("input", onInput);
      autocomplete.addEventListener("change", onInput);
      autocomplete.addEventListener("gmp-error", onError);
      host.replaceChildren(autocomplete);
    });
    return () => {
      cancelled = true;
      unbindCapture?.();
      if (element && onSelect) element.removeEventListener("gmp-select", onSelect, true);
      if (element && onInput) {
        element.removeEventListener("input", onInput);
        element.removeEventListener("change", onInput);
      }
      if (element && onError) element.removeEventListener("gmp-error", onError);
      element?.remove();
      host.replaceChildren();
    };
  }, [useGoogleStreet]);

  useEffect(() => {
    if (!placeId || picked) return;
    const host = detailsRef.current;
    if (!host) return;
    let cancelled = false;
    setLookupError("");
    setResolvingPlace(true);
    void resolvePlaceAddressFromId(placeId, host, {
      requirePostalCode: true,
    }).then((parsed) => {
      if (cancelled) return;
      googleSelectLockRef.current = false;
      setResolvingPlace(false);
      if (!parsed) {
        setLookupError("Could not load that address. Enter city, state, and ZIP.");
        setPlaceId(null);
        return;
      }
      setValues(parsed);
      setPicked(true);
      setExisting([]);
      setLookupError("");
      setZipError(parsed.postalCode ? "" : "Enter ZIP.");
      onResolvedRef.current?.(parsed);
    });
    return () => {
      cancelled = true;
      setResolvingPlace(false);
    };
  }, [placeId, picked]);

  function chooseExisting(hit: ExistingAddressHit) {
    const address = streetFromHit(hit);
    setValues(address);
    setPicked(true);
    setPlaceId(null);
    setExisting([]);
    setLookupError("");
    setZipError("");
    googleSelectLockRef.current = false;
    onExistingSelectRef.current?.(hit.row);
  }

  const streetError = lookupError || undefined;

  return (
    <div className="relative space-y-2">
      {legend && <SectionLabel>{legend}</SectionLabel>}
      <div className="grid gap-2 sm:grid-cols-6">
        <div className="sm:col-span-4">
          {useGoogleStreet ? (
            <Field
              label="Street address"
              required
              labelId={streetLabelId}
              hint={resolvingPlace ? "Looking up that street…" : undefined}
              error={streetError}
            >
              <div
                ref={hostRef}
                className={`gmp-autocomplete-host${
                  hits.length > 0 || (!existingReady && needle.length >= 3)
                    ? " hide-google-predictions"
                    : ""
                }`}
              />
              <input type="hidden" name={field("addressLine1")} value={values.addressLine1} />
            </Field>
          ) : (
            <TextInput
              required
              label="Street address"
              name={field("addressLine1")}
              autoComplete="off"
              value={values.addressLine1}
              error={streetError}
              onChange={(event) => {
                setPicked(false);
                setPlaceId(null);
                setLookupError("");
                setZipError("");
                setValues((current) => ({ ...current, addressLine1: event.target.value }));
              }}
            />
          )}
          {enableLookup && !picked && hits.length > 0 && (
            <ul role="listbox" aria-label="Addresses on file" className="mt-1 divide-y divide-[var(--line)] overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)] shadow-sm">
              {hits.map((hit) => (
                <li key={hit.key} role="none">
                  <button
                    type="button"
                    role="option"
                    aria-label={`${hit.row.firstName} ${hit.row.lastName}, ${hitKindLabel(hit.kind)}: ${hit.address.addressLine1}`}
                    onClick={() => chooseExisting(hit)}
                    className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left hover:bg-[var(--wash)]"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-[var(--ink)]">
                        {hit.row.firstName} {hit.row.lastName}
                      </span>
                      <span className="block truncate text-[12px] text-[var(--ink-soft)]">
                        {hit.address.addressLine1}, {hit.address.city} {hit.address.postalCode ?? ""}
                      </span>
                    </span>
                    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[.06em] text-[var(--ink-soft)]">
                      {hitKindLabel(hit.kind)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {picked && enableLookup && (
            <button
              type="button"
              aria-label="Change street address"
              onClick={() => {
                googleSelectLockRef.current = false;
                setPicked(false);
                setPlaceId(null);
                setLookupError("");
                setZipError("");
                setValues(emptyAddress);
                setExisting([]);
              }}
              className="mt-1 text-xs font-semibold text-[var(--accent)]"
            >
              Change
            </button>
          )}
        </div>
        <div className="sm:col-span-2">
          <TextInput
            label="Unit"
            name={field("addressLine2")}
            autoComplete="off"
            value={values.addressLine2 ?? ""}
            onChange={(event) =>
              setValues((current) => ({
                ...current,
                addressLine2: event.target.value.trim() ? event.target.value : null,
              }))
            }
          />
        </div>
        <div className="sm:col-span-3">
          <TextInput
            required
            label="City"
            name={field("city")}
            autoComplete="off"
            value={values.city}
            onChange={(event) => setValues((current) => ({ ...current, city: event.target.value }))}
          />
        </div>
        <div className="sm:col-span-1">
          <TextInput
            required
            label="State"
            name={field("state")}
            maxLength={2}
            autoComplete="off"
            value={values.state}
            onChange={(event) => setValues((current) => ({ ...current, state: event.target.value }))}
          />
        </div>
        <div className="sm:col-span-2">
          <TextInput
            required
            label="ZIP"
            name={field("postalCode")}
            autoComplete="off"
            value={values.postalCode}
            hint={picked && resolvingPlace ? "Looking up that street…" : undefined}
            error={zipError || undefined}
            onChange={(event) => {
              setZipError("");
              setValues((current) => ({ ...current, postalCode: event.target.value }));
            }}
          />
        </div>
      </div>
      <input type="hidden" name={field("lat")} value={values.lat ?? ""} />
      <input type="hidden" name={field("lng")} value={values.lng ?? ""} />
      <div
        ref={detailsRef}
        aria-hidden="true"
        className={`gmp-place-details-host${resolvingPlace ? " is-resolving" : ""}`}
      />
    </div>
  );
}

export function AddressAlreadyOnFile({
  rows,
  onChoose,
}: {
  rows: Array<{
    id: string;
    firstName: string;
    lastName: string;
    primaryPhone?: string | null;
    email?: string | null;
    matchingProperty?: { addressLine1: string; city: string; postalCode?: string | null } | null;
    matchingBilling?: { addressLine1: string; city: string; postalCode?: string | null } | null;
  }>;
  onChoose: (id: string) => void;
}) {
  if (!rows.length) return null;
  return (
    <div className="rounded-lg border border-[var(--st-alert)] bg-[var(--accent-wash)] p-3">
      <p className="text-[13px] font-semibold text-[var(--ink)]">
        {rows.length === 1
          ? "That address is already on file."
          : "Those addresses are already on file."}
      </p>
      <p className="mt-0.5 text-[12px] text-[var(--ink-soft)]">
        Booking a second one splits their history and what they owe.
      </p>
      <div className="mt-2 space-y-1">
        {rows.map((row) => (
          <button
            key={row.id}
            type="button"
            onClick={() => onChoose(row.id)}
            className="block w-full rounded-lg bg-[var(--paper)] px-3 py-2 text-left hover:bg-[var(--wash)]"
          >
            <span className="block text-sm font-semibold text-[var(--ink)]">
              {row.firstName} {row.lastName}
            </span>
            <span className="mt-0.5 block truncate text-[12px] text-[var(--ink-soft)]">
              {row.matchingProperty
                ? `${row.matchingProperty.addressLine1}, ${row.matchingProperty.city} ${row.matchingProperty.postalCode ?? ""}`
                : row.matchingBilling
                  ? `${row.matchingBilling.addressLine1}, ${row.matchingBilling.city} ${row.matchingBilling.postalCode ?? ""}`
                  : row.primaryPhone
                    ? phoneNumber(row.primaryPhone)
                    : row.email || "Use this customer instead"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
